import React, { useEffect, useRef, useState } from 'react';
import aerialMark from '../assets/aerial-mark.svg';
import { ACCESS_CODE_TARGETS } from '../constants/authConfig';

export default function Login({ onAuthenticated }) {
  const [accessCode, setAccessCode] = useState('');
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const errorTimerRef = useRef(null);
  const loadingTimerRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();

    return () => {
      window.clearTimeout(errorTimerRef.current);
      window.clearTimeout(loadingTimerRef.current);
    };
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const authTarget = ACCESS_CODE_TARGETS[accessCode];

    if (authTarget) {
      setShowError(false);
      setLoading(true);
      loadingTimerRef.current = window.setTimeout(() => {
        setLoading(false);
        onAuthenticated(authTarget);
      }, 400);
      return;
    }

    setAccessCode('');
    window.clearTimeout(errorTimerRef.current);
    setShowError(true);
    errorTimerRef.current = window.setTimeout(() => {
      setShowError(false);
    }, 3000);
    inputRef.current?.focus();
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#0a0f1e]">
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 85% 68% at 18% 82%, rgba(15,39,68,0.9) 0%, transparent 60%),
            radial-gradient(ellipse 60% 54% at 82% 18%, rgba(22,47,86,0.78) 0%, transparent 55%),
            radial-gradient(ellipse 40% 36% at 52% 50%, rgba(11,60,114,0.34) 0%, transparent 52%),
            #0a0f1e
          `,
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-100"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-5 py-10">
        <div className="w-full max-w-[26rem]">
          <div className="mb-10 text-center">
            <div className="mb-5 flex justify-center">
              <img
                src={aerialMark}
                alt=""
                aria-hidden="true"
                className="h-[5.25rem] w-auto drop-shadow-[0_12px_28px_rgba(0,0,0,0.28)]"
              />
            </div>
            <div className="text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-white">
              Aerial Telecom
            </div>
            <div className="mt-2 text-sm text-white/40">Jio Ops Dashboard</div>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-[12px] font-medium text-white/50">
              <span className="font-semibold text-white/80">Aerial Telecom Services</span>
              <span className="text-white/25">·</span>
              <span>Contract Performance Dashboard</span>
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-[20px] sm:p-9">
            <h1 className="text-[18px] font-bold text-white">Welcome back</h1>
            <p className="mt-1.5 text-[13px] leading-relaxed text-white/40">
              Enter your access code to view the dashboard.
            </p>

            {showError ? (
              <div className="mt-5 rounded-lg border border-red-500/20 bg-red-500/10 px-3.5 py-2.5 text-[12px] font-medium text-red-400">
                Incorrect access code. Please try again.
              </div>
            ) : null}

            <form className="mt-6" onSubmit={handleSubmit}>
              <label className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.08em] text-white/35" htmlFor="access-code">
                Access Code
              </label>
              <input
                ref={inputRef}
                id="access-code"
                type="password"
                autoComplete="off"
                value={accessCode}
                onChange={(event) => setAccessCode(event.target.value)}
                placeholder="Enter your access code"
                className="w-full rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3.5 text-[15px] text-white outline-none transition duration-200 placeholder:text-white/20 focus:border-blue-500/60 focus:bg-white/[0.08] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.15)]"
              />

              <button
                type="submit"
                disabled={loading}
                className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] px-4 py-3.5 text-[14px] font-bold tracking-[0.02em] text-white transition duration-200 hover:-translate-y-px hover:shadow-[0_12px_28px_rgba(37,99,235,0.32)] disabled:pointer-events-none disabled:opacity-70"
              >
                {loading ? 'Loading…' : 'Access Dashboard'}
              </button>
            </form>
          </div>

          <div className="mt-6 text-center text-[11px] leading-relaxed text-white/20">
            Powered by{' '}
            <a
              className="text-sky-300/50 transition duration-200 hover:text-sky-300/70"
              href="https://auditbuddy.co"
              target="_blank"
              rel="noreferrer"
            >
              AuditBuddy
            </a>{' '}
            · Confidential
          </div>
        </div>
      </div>
    </div>
  );
}
